import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  Facebook,
  Instagram,
  YouTube,
  Email,
  Phone,
  Twitter,
} from "@mui/icons-material";
import PageHeader from "../../example/pages-header/PageHeader";
import useAdd from "../../api/useAdd";
import SEO from "../../components/ceo/CEO";

const ContactUs = () => {
  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState("");

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [subject, setSubject] = useState("");
  const [subjectError, setSubjectError] = useState("");

  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");

  const { handleAdd, loading, resetForm } = useAdd(
    "contact",
    null,
    "Your message successfully sent!",
    "Failed operation",
  );

  useEffect(() => {
    if (resetForm) {
      setFullName("");
      setEmail("");
      setSubject("");
      setMessage("");
    }
  }, [resetForm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let isValid = true;

    if (!fullName) {
      setFullNameError("Full name is required");
      isValid = false;
    } else {
      setFullNameError("");
    }

    if (!email) {
      setEmailError("Email is required");
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!subject) {
      setSubjectError("Subject is required");
      isValid = false;
    } else {
      setSubjectError("");
    }

    if (!message) {
      setMessageError("Message is required");
      isValid = false;
    } else {
      setMessageError("");
    }

    if (isValid) {
      const formData = new FormData();
      formData.append("full_name", fullName);
      formData.append("email", email);
      formData.append("subject", subject);
      formData.append("message", message);

      handleAdd(formData);
    }
  };

  return (
    <Fragment>
      <SEO
        title="Contact Us"
        description="Reach out to SHALL Social and Development Organization through our contact form. We value your feedback and look forward to hearing from you."
        url="https://shallafg.org/contact/"
        image="https://shallafg.org/media/images/logo.png"
      />

      <PageHeader
        title="Do you have any points to share with us?"
        subtitle="We will be glad to hear from you! Your feedback is valuable and helps us improve our services."
      />
      <Box
        sx={{
          backgroundImage: "linear-gradient(to right, #0B0F19, #1A2E55)",
          color: "#FFFFFF",
          minHeight: "100vh",
          paddingY: "60px",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Contact Information Cards */}
            {[
              {
                icon: <Phone sx={{ fontSize: "3rem", color: "#A9C9EA" }} />,
                title: "Phone",
                content: "+1 902-402-5219",
              },
              {
                icon: (
                  <Email
                    sx={{
                      cursor: "pointer",
                      fontSize: "3rem",
                      color: "#A9C9EA",
                      "&:hover": { color: "#00BFFF" },
                    }}
                    onClick={() =>
                      (window.location.href =
                        "mailto:Foundationshall@gmail.com")
                    }
                  />
                ),
                title: "Email",
                content: "Foundationshall@gmail.com",
              },
              {
                icon: (
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      href="https://www.facebook.com/shallFoundation"
                      target="_blank"
                      sx={{ color: "#A9C9EA", "&:hover": { color: "#00BFFF" } }}
                    >
                      <Facebook />
                    </IconButton>
                    <IconButton
                      href="https://www.youtube.com/channel/UCIA02ec8AR4A2kQSniP139g"
                      target="_blank"
                      sx={{ color: "#A9C9EA", "&:hover": { color: "#00BFFF" } }}
                    >
                      <YouTube />
                    </IconButton>
                    <IconButton
                      href="https://www.instagram.com"
                      target="_blank"
                      sx={{ color: "#A9C9EA", "&:hover": { color: "#00BFFF" } }}
                    >
                      <Instagram />
                    </IconButton>
                    <IconButton
                      href="https://x.com/AYFDA_Official"
                      target="_blank"
                      sx={{ color: "#A9C9EA", "&:hover": { color: "#00BFFF" } }}
                    >
                      <Twitter />
                    </IconButton>
                  </Stack>
                ),
                title: "Follow Us",
                content: "Stay connected through social media",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "16px",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.3)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 12px 36px rgba(0, 123, 255, 0.4)",
                    },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    padding: "20px",
                    height: "100%",
                  }}
                >
                  {item.icon}
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#A9C9EA",
                        marginBottom: "10px",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#D1D5DB" }}>
                      {item.content}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Contact Form Section */}
          <Box
            sx={{
              marginTop: "40px",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              borderRadius: "16px",
              padding: "40px",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                marginBottom: "24px",
                color: "#A9C9EA",
                textAlign: "center",
              }}
            >
              Send Us a Message
            </Typography>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Name"
                  variant="filled"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  error={Boolean(fullNameError)}
                  helperText={fullNameError}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      color: "#FFFFFF",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#A9C9EA",
                    },
                  }}
                />
                <TextField
                  label="Email"
                  variant="filled"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={Boolean(emailError)}
                  helperText={emailError}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      color: "#FFFFFF",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#A9C9EA",
                    },
                  }}
                />
                <TextField
                  label="Subject"
                  variant="filled"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  error={Boolean(subjectError)}
                  helperText={subjectError}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      color: "#FFFFFF",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#A9C9EA",
                    },
                  }}
                />
                <TextField
                  label="Message"
                  variant="filled"
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  error={Boolean(messageError)}
                  helperText={messageError}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      color: "#FFFFFF",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#A9C9EA",
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: "#10a7e0",
                    color: "#FFFFFF",
                    borderRadius: "30px",
                    padding: "12px 30px",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                    boxShadow: "0 6px 20px rgba(16, 167, 224, 0.4)",
                    "&:hover": {
                      backgroundColor: "#0056b3",
                      boxShadow: "0 8px 24px rgba(0, 86, 179, 0.5)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Stack>
            </form>
          </Box>
        </Container>
      </Box>
    </Fragment>
  );
};

export default ContactUs;
